import { useEffect } from "react";
import { useGameStore } from "../../app/gameStore";
import { useInteractionStore } from "../../render/iso/interactionStore";
import "./toast.css";

const DISMISS_MS = 2400;

/**
 * Surfaces the engine's refusals.
 *
 * `playCard` validates before it mutates, so a rejected card costs nothing and
 * leaves the rig untouched — which is correct, and reads as a broken UI if
 * nothing says why. The engine already carries the reason; this just shows it.
 */
export function Toast() {
  const rejection = useGameStore((s) => s.rejection);
  const clearRejection = useGameStore((s) => s.clearRejection);

  useEffect(() => {
    if (!rejection) return;
    const timer = window.setTimeout(clearRejection, DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [rejection, clearRejection]);

  if (!rejection) return null;

  // Keyed on the text so an identical repeat still replays the shake — the
  // player pressed again and deserves to see that it was refused again.
  return (
    <div className="toast" key={rejection} role="status">
      {rejection}
    </div>
  );
}

const NOTICE_MS = 5200;

/**
 * Deck pollution and other things worth reading but not worth interrupting for.
 *
 * A hazard card appearing in a later hand with no explanation reads as the game
 * cheating, so §3.2's "shuffled in when maintenance is neglected" has to be
 * said out loud, naming both the card and the neglect. It lingers longer than a
 * refusal because it is news, not feedback on an action just taken.
 */
export function Notice() {
  const notice = useGameStore((s) => s.notice);
  const clearNotice = useGameStore((s) => s.clearNotice);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(clearNotice, NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [notice, clearNotice]);

  if (!notice) return null;

  return (
    <div className="notice" key={notice} role="status">
      <span className="notice__mark">!</span>
      {notice}
    </div>
  );
}

/**
 * A pipe card is mid-placement, waiting for its far end. Pipes are edges, not
 * tiles, so this is the one card that cannot be committed by a single drop.
 */
export function PipeTargeting() {
  const pendingPipe = useGameStore((s) => s.pendingPipe);
  const cancelPipe = useGameStore((s) => s.cancelPipe);
  const hoveredTile = useInteractionStore((s) => s.hoveredTile);

  useEffect(() => {
    if (!pendingPipe) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelPipe();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingPipe, cancelPipe]);

  if (!pendingPipe) return null;

  return (
    <div className="pipe-targeting">
      <span className="pipe-targeting__dot" />
      pick the machine this pipe feeds
      {/* Same readout as the drag hint, so aiming reads the same either way. */}
      {hoveredTile && (
        <span className="pipe-targeting__at">
          over {hoveredTile.x},{hoveredTile.y}
        </span>
      )}
      <button onClick={cancelPipe}>esc to cancel</button>
    </div>
  );
}
